/**
 * The settings scope context: what the URL axes resolve to, and how to change
 * them.
 *
 * Adapted from upstream's `SettingsScopeContext.tsx` (pingdotgg/t3code, MIT,
 * pin e3e7cc3f). Upstream also resolves per-member project files and write
 * targets here; forge's pages write where they write today, so this context
 * carries the read model only: the resolved scope, the menus' options, and
 * the selected target. Pages adopt it at their own pace.
 *
 * The provider mounts inside the settings route, where the router exists; the
 * sentence and boundary read the context, so they stay renderable in suites
 * that mount settings panels without a router.
 *
 * @module
 */

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { useCmmdEnvironmentListSnapshot } from "../../environments/cmmdEnvironments/cmmdEnvironmentListResource";
import { useEnvironmentStoreSnapshot } from "../../hooks/useEnvironmentStoreSnapshot";
import { useProjectsAcrossEnvironments } from "../../hooks/useProjectsAcrossEnvironments";
import { useProjectGroupingStore } from "../../projectGroupingStore";
import { selectBootstrapCompleteForActiveEnvironment } from "../../store";

import { buildSettingsScopeGroups } from "./settingsScopeGroups";
import {
  resolveSettingsScope,
  type ResolvedSettingsScope,
  type SettingsScopeEnvironment,
  type SettingsScopeGroup,
  type SettingsScopeSearch,
} from "./settingsScope";

/** A scope menu row: the pure environment plus whether it is reachable. */
export interface SettingsScopeEnvironmentEntry extends SettingsScopeEnvironment {
  readonly online: boolean;
}

export interface SettingsScopeContextValue {
  readonly search: SettingsScopeSearch;
  /** The pathname the provider mounted on; device-only pages read it. */
  readonly pathname: string;
  readonly scope: ResolvedSettingsScope;
  readonly groups: readonly SettingsScopeGroup[];
  readonly environments: readonly SettingsScopeEnvironmentEntry[];
  /**
   * False while the data both axes resolve against has not settled once: the
   * CMMD list starts empty (reporting not-loading on the very first render)
   * and the project store starts with no environment state. A boundary that
   * declared "unavailable" during that window would be lying.
   */
  readonly scopeDataReady: boolean;
  readonly selectScope: (next: SettingsScopeSearch) => void;
}

const SettingsScopeContext = createContext<SettingsScopeContextValue | null>(null);

/** The CMMD environment list as scope rows: one per connectable environment. */
function useSettingsScopeEnvironments(): readonly SettingsScopeEnvironmentEntry[] {
  const snapshot = useCmmdEnvironmentListSnapshot();
  return useMemo(() => {
    const byId = new Map<string, SettingsScopeEnvironmentEntry>();
    for (const entry of snapshot.environments) {
      // A cloud sprite that CMMD has not finished registering has no durable
      // environment yet; its runtime id is what connections still use.
      const environmentId =
        entry.kind === "cloud-sprite" ? (entry.environmentId ?? entry.id) : entry.id;
      if (!byId.has(environmentId)) {
        byId.set(environmentId, { environmentId, label: entry.label, online: entry.online });
      }
    }
    return [...byId.values()];
  }, [snapshot.environments]);
}

/**
 * The CMMD list has settled once it shows data, names an error, or has been
 * through a loading cycle that ended. An empty, errorless, never-loading
 * snapshot is the transport's first render, not an answer.
 */
function useCmmdEnvironmentListSettled(): { settled: boolean; completedEmpty: boolean } {
  const snapshot = useCmmdEnvironmentListSnapshot();
  const seenLoadingRef = useRef(false);
  const [settled, setSettled] = useState(false);
  useEffect(() => {
    if (snapshot.isLoading) seenLoadingRef.current = true;
    if (settled) return;
    if (
      snapshot.environments.length > 0 ||
      snapshot.error !== null ||
      (seenLoadingRef.current && !snapshot.isLoading && !snapshot.stale)
    ) {
      setSettled(true);
    }
  }, [settled, snapshot]);
  return { settled, completedEmpty: settled && snapshot.environments.length === 0 };
}

export function SettingsScopeProvider(props: {
  readonly search: SettingsScopeSearch;
  readonly pathname: string;
  readonly onChange: (next: SettingsScopeSearch) => void;
  readonly children: ReactNode;
}) {
  const projects = useProjectsAcrossEnvironments();
  const groupingOverrides = useProjectGroupingStore((state) => state.overrides);
  const environments = useSettingsScopeEnvironments();
  const projectsReady = selectBootstrapCompleteForActiveEnvironment(useEnvironmentStoreSnapshot());
  const { settled: cmmdSettled, completedEmpty: cmmdInventoryEmpty } =
    useCmmdEnvironmentListSettled();
  // A completed empty CMMD inventory is an answer too: with no environments
  // anywhere, the project store can never bootstrap, so waiting on it would
  // hold a bookmarked stale scope in limbo instead of explaining it.
  const scopeDataReady = cmmdSettled && (projectsReady || cmmdInventoryEmpty);
  const groups = useMemo(
    () => buildSettingsScopeGroups(projects, groupingOverrides),
    [groupingOverrides, projects],
  );
  const value = useMemo<SettingsScopeContextValue>(() => {
    const scope = resolveSettingsScope(props.search, groups, environments);
    return {
      search: props.search,
      pathname: props.pathname,
      scope,
      groups,
      environments,
      scopeDataReady,
      selectScope: props.onChange,
    };
  }, [environments, groups, props, scopeDataReady]);
  return <SettingsScopeContext value={value}>{props.children}</SettingsScopeContext>;
}

/** Null outside the settings route, so panels that render standalone stay mounted. */
export function useOptionalSettingsScope(): SettingsScopeContextValue | null {
  return useContext(SettingsScopeContext);
}
